/**
 * Webtech HR Engine™ - SuperAdmin AI Supervision Dashboard
 * Phase 4: Supervision de l'IA - suivi utilisation, activation/désactivation par client
 */

import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Progress } from './ui/progress';
import { Switch } from './ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from './ui/dialog';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Skeleton } from './ui/skeleton';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog';
import api from '../lib/api';
import { toast } from 'sonner';
import {
  Brain,
  Building2,
  TrendingUp,
  TrendingDown,
  Activity,
  Zap,
  Target,
  Users,
  DollarSign,
  Clock,
  Calendar,
  RefreshCw,
  Search,
  Filter,
  MoreVertical,
  Eye,
  Power,
  PowerOff,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Sparkles,
  BarChart3,
  LineChart,
  Loader2,
  ChevronRight,
  Info
} from 'lucide-react';

// Formatage date relative
const formatRelativeDate = (dateString) => {
  if (!dateString) return 'Jamais';
  const date = new Date(dateString);
  const now = new Date();
  const diff = Math.floor((now - date) / (1000 * 60 * 60 * 24));
  
  if (diff === 0) return "Aujourd'hui";
  if (diff === 1) return 'Hier';
  if (diff < 7) return `Il y a ${diff} jours`;
  if (diff < 30) return `Il y a ${Math.floor(diff / 7)} semaines`;
  return date.toLocaleDateString('fr-FR');
};

// Formatage monétaire
const formatCurrency = (amount, currency = 'USD') => {
  if (currency === 'USD') {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(amount);
  }
  return `${amount} ${currency}`;
};

// KPI Card Component
function AIKPICard({ title, value, subtitle, icon: Icon, color, trend, loading }) {
  const colorClasses = {
    purple: 'from-purple-500 to-purple-600',
    blue: 'from-blue-500 to-blue-600',
    green: 'from-emerald-500 to-emerald-600',
    amber: 'from-amber-500 to-amber-600',
    red: 'from-red-500 to-red-600',
    indigo: 'from-indigo-500 to-indigo-600'
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-start justify-between">
            <div className="space-y-2">
              <Skeleton className="h-4 w-24" />
              <Skeleton className="h-8 w-32" />
              <Skeleton className="h-3 w-20" />
            </div>
            <Skeleton className="h-12 w-12 rounded-xl" />
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent className="pt-6">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-sm font-medium text-slate-500">{title}</p>
            <p className="text-2xl font-bold text-slate-900 mt-1">{value}</p>
            <div className="flex items-center gap-2 mt-2">
              {trend && (
                <span className={`flex items-center text-xs font-medium ${
                  trend.direction === 'up' ? 'text-emerald-600' : 'text-red-600'
                }`}>
                  {trend.direction === 'up' ? 
                    <TrendingUp className="w-3 h-3 mr-0.5" /> : 
                    <TrendingDown className="w-3 h-3 mr-0.5" />
                  }
                  {trend.value}
                </span>
              )}
              {subtitle && <span className="text-xs text-slate-500">{subtitle}</span>}
            </div>
          </div>
          <div className={`p-3 rounded-xl bg-gradient-to-br ${colorClasses[color] || colorClasses.purple} shadow-lg`}>
            <Icon className="w-6 h-6 text-white" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// Analysis Type Badge
function AnalysisTypeBadge({ type }) {
  const typeConfig = {
    performance: { label: 'Performance', class: 'bg-blue-100 text-blue-700', icon: Target },
    skills: { label: 'Compétences', class: 'bg-purple-100 text-purple-700', icon: Zap },
    team: { label: 'Équipe', class: 'bg-emerald-100 text-emerald-700', icon: Users },
    skills_gaps: { label: 'Écarts compétences', class: 'bg-amber-100 text-amber-700', icon: BarChart3 }
  };

  const config = typeConfig[type] || { label: type || 'N/A', class: 'bg-slate-100 text-slate-700', icon: Brain };
  const Icon = config.icon;

  return (
    <Badge className={`${config.class} font-medium`}>
      <Icon className="w-3 h-3 mr-1" />
      {config.label}
    </Badge>
  );
}

// Toggle AI Dialog
function ToggleAIDialog({ isOpen, onClose, company, onSuccess }) {
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);
  const newStatus = !company?.aiEnabled;

  const handleSubmit = async () => {
    setLoading(true);
    try {
      await api.post(`/superadmin/ai/companies/${company.companyId}/toggle`, {
        enabled: newStatus,
        reason: reason || undefined
      });
      toast.success(`IA ${newStatus ? 'activée' : 'désactivée'} pour ${company.companyName}`);
      onSuccess();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la modification");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {newStatus ? (
              <Power className="w-5 h-5 text-emerald-500" />
            ) : (
              <PowerOff className="w-5 h-5 text-red-500" />
            )}
            {newStatus ? 'Activer' : 'Désactiver'} l&apos;IA
          </DialogTitle>
          <DialogDescription>
            {newStatus 
              ? `Activer le module IA pour ${company?.companyName}`
              : `Désactiver le module IA pour ${company?.companyName}`
            }
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {!newStatus && (
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-700">
              <AlertTriangle className="w-4 h-4 inline mr-2" />
              L&apos;entreprise ne pourra plus lancer d&apos;analyses IA.
            </div>
          )}

          <div className="space-y-2">
            <Label>Raison (optionnel)</Label>
            <Textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Motif de cette action..."
              rows={3}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annuler</Button>
          <Button 
            onClick={handleSubmit} 
            disabled={loading}
            className={newStatus ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-red-600 hover:bg-red-700'}
          >
            {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            {newStatus ? 'Activer' : 'Désactiver'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Company AI Details Dialog
function CompanyAIDetailsDialog({ isOpen, onClose, companyId }) {
  const [details, setDetails] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isOpen && companyId) {
      fetchDetails();
    }
  }, [isOpen, companyId]);

  const fetchDetails = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/superadmin/ai/company/${companyId}/details`);
      setDetails(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des détails');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-3xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Brain className="w-5 h-5 text-purple-500" />
            Détails IA - {details?.company?.name || 'Chargement...'}
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-purple-500" />
          </div>
        ) : details ? (
          <div className="space-y-6 py-4">
            {/* Status */}
            <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
              <div className="flex items-center gap-3">
                {details.subscription?.aiEnabled ? (
                  <CheckCircle2 className="w-6 h-6 text-emerald-500" />
                ) : (
                  <XCircle className="w-6 h-6 text-red-500" />
                )}
                <div>
                  <p className="font-medium text-slate-900">
                    Module IA {details.subscription?.aiEnabled ? 'Activé' : 'Désactivé'}
                  </p>
                  {details.subscription?.aiToggledAt && (
                    <p className="text-xs text-slate-500">
                      Modifié le {formatRelativeDate(details.subscription.aiToggledAt)} 
                      {details.subscription.aiToggledBy && ` par ${details.subscription.aiToggledBy}`}
                    </p>
                  )}
                </div>
              </div>
              <Badge className={details.subscription?.plan === 'pro' ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-700'}>
                {details.subscription?.plan?.toUpperCase() || 'N/A'}
              </Badge>
            </div>

            {/* Usage Stats */}
            <div className="grid grid-cols-3 gap-4">
              <div className="bg-purple-50 rounded-xl p-4 text-center">
                <p className="text-3xl font-bold text-purple-700">{details.usage?.totalAnalyses || 0}</p>
                <p className="text-sm text-purple-600">Analyses totales</p>
              </div>
              <div className="bg-blue-50 rounded-xl p-4 text-center">
                <p className="text-3xl font-bold text-blue-700">{details.usage?.thisMonth || 0}</p>
                <p className="text-sm text-blue-600">Ce mois</p>
              </div>
              <div className="bg-emerald-50 rounded-xl p-4 text-center">
                <p className="text-3xl font-bold text-emerald-700">{formatCurrency(details.usage?.estimatedCostUSD || 0)}</p>
                <p className="text-sm text-emerald-600">Coût estimé</p>
              </div>
            </div>

            {/* By Type */}
            {details.usage?.byType && Object.keys(details.usage.byType).length > 0 && (
              <div>
                <h4 className="font-medium text-slate-900 mb-3">Répartition par type</h4>
                <div className="space-y-2">
                  {Object.entries(details.usage.byType).map(([type, count]) => {
                    const total = details.usage.totalAnalyses || 1;
                    const percentage = Math.round((count / total) * 100);
                    return (
                      <div key={type} className="space-y-1">
                        <div className="flex items-center justify-between text-sm">
                          <AnalysisTypeBadge type={type} />
                          <span className="font-medium">{count} ({percentage}%)</span>
                        </div>
                        <Progress value={percentage} className="h-2" />
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Monthly Trend */}
            {details.usage?.monthlyTrend && details.usage.monthlyTrend.length > 0 && (
              <div>
                <h4 className="font-medium text-slate-900 mb-3">Tendance mensuelle</h4>
                <div className="flex items-end gap-2 h-24">
                  {details.usage.monthlyTrend.map((item, idx) => {
                    const maxCount = Math.max(...details.usage.monthlyTrend.map(m => m.count)) || 1;
                    const height = (item.count / maxCount) * 100;
                    return (
                      <div key={idx} className="flex-1 flex flex-col items-center">
                        <div 
                          className="w-full bg-purple-500 rounded-t"
                          style={{ height: `${Math.max(height, 5)}%` }}
                        />
                        <span className="text-xs text-slate-500 mt-1">{item.month.slice(5)}</span>
                        <span className="text-xs font-medium">{item.count}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Top Users */}
            {details.topUsers && details.topUsers.length > 0 && (
              <div>
                <h4 className="font-medium text-slate-900 mb-3">Utilisateurs les plus actifs</h4>
                <div className="space-y-2">
                  {details.topUsers.map((user, idx) => (
                    <div key={idx} className="flex items-center justify-between p-2 bg-slate-50 rounded-lg">
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center text-purple-700 font-medium text-sm">
                          {idx + 1}
                        </div>
                        <div>
                          <p className="text-sm font-medium text-slate-900">{user.userName}</p>
                          <p className="text-xs text-slate-500">{user.email}</p>
                        </div>
                      </div>
                      <Badge variant="secondary">{user.analysisCount} analyses</Badge>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Recent Analyses */}
            {details.recentAnalyses && details.recentAnalyses.length > 0 && (
              <div>
                <h4 className="font-medium text-slate-900 mb-3">Dernières analyses</h4>
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {details.recentAnalyses.map((analysis, idx) => (
                    <div key={idx} className="flex items-center justify-between p-2 bg-slate-50 rounded-lg">
                      <div className="flex items-center gap-2">
                        <AnalysisTypeBadge type={analysis.analysisType} />
                        {analysis.employeeName && (
                          <span className="text-sm text-slate-600">{analysis.employeeName}</span>
                        )}
                      </div>
                      <span className="text-xs text-slate-500">
                        {formatRelativeDate(analysis.createdAt)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="text-center py-8 text-slate-500">
            Aucune donnée disponible
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// Global AI Toggle Confirmation
function GlobalAIToggleDialog({ isOpen, onClose, currentlyEnabled, onSuccess }) {
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);
  const newStatus = !currentlyEnabled;

  const handleSubmit = async () => {
    if (!reason.trim()) {
      toast.error('Veuillez indiquer une raison');
      return;
    }

    setLoading(true);
    try {
      await api.post(`/superadmin/ai/global/toggle?enabled=${newStatus}&reason=${encodeURIComponent(reason)}`);
      toast.success(`IA ${newStatus ? 'activée' : 'désactivée'} globalement`);
      onSuccess();
      onClose();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la modification");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AlertDialog open={isOpen} onOpenChange={onClose}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2 text-red-600">
            <AlertTriangle className="w-5 h-5" />
            Action globale - {newStatus ? 'Activer' : 'Désactiver'} l&apos;IA
          </AlertDialogTitle>
          <AlertDialogDescription>
            Cette action va {newStatus ? 'activer' : 'désactiver'} le module IA pour <strong>TOUTES</strong> les entreprises PRO.
            Cette action est impactante et sera journalisée.
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="py-4">
          <Label>Raison (obligatoire)</Label>
          <Textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Motif de cette action globale..."
            rows={3}
            className="mt-2"
          />
        </div>

        <AlertDialogFooter>
          <AlertDialogCancel>Annuler</AlertDialogCancel>
          <AlertDialogAction 
            onClick={handleSubmit}
            disabled={loading || !reason.trim()}
            className={newStatus ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-red-600 hover:bg-red-700'}
          >
            {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Confirmer
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

// Main Component
export default function SuperAdminAIDashboard() {
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState(null);
  const [companies, setCompanies] = useState([]);
  
  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [aiFilter, setAiFilter] = useState('all');
  const [sortBy, setSortBy] = useState('analysisCount');
  
  // Dialogs
  const [toggleDialog, setToggleDialog] = useState({ open: false, company: null });
  const [detailsDialog, setDetailsDialog] = useState({ open: false, companyId: null });
  const [globalToggleDialog, setGlobalToggleDialog] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [overviewRes, companiesRes] = await Promise.all([
        api.get('/superadmin/ai/overview'),
        api.get('/superadmin/ai/companies')
      ]);
      
      setOverview(overviewRes.data);
      setCompanies(companiesRes.data.companies || []);
    } catch (error) {
      console.error('Error fetching AI data:', error);
      toast.error('Erreur lors du chargement des données IA');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Filter companies
  const filteredCompanies = companies.filter(company => {
    const matchesSearch = !searchTerm || 
      company.companyName?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesAI = aiFilter === 'all' || 
      (aiFilter === 'enabled' && company.aiEnabled) ||
      (aiFilter === 'disabled' && !company.aiEnabled);
    
    return matchesSearch && matchesAI;
  });

  // Sort companies
  const sortedCompanies = [...filteredCompanies].sort((a, b) => {
    switch (sortBy) {
      case 'companyName':
        return a.companyName.localeCompare(b.companyName);
      case 'analysesThisMonth':
        return b.analysesThisMonth - a.analysesThisMonth;
      case 'lastAnalysisAt':
        return (b.lastAnalysisAt || '').localeCompare(a.lastAnalysisAt || '');
      default: // analysisCount
        return b.totalAnalyses - a.totalAnalyses;
    }
  });

  return (
    <div className="space-y-6">
      {/* Header KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-6 gap-4">
        <AIKPICard
          title="Analyses totales"
          value={overview?.summary?.totalAnalyses || 0}
          subtitle={`+${overview?.summary?.thisMonth || 0} ce mois`}
          icon={Brain}
          color="purple"
          loading={loading}
        />
        <AIKPICard
          title="Ce mois"
          value={overview?.summary?.thisMonth || 0}
          subtitle={`${overview?.summary?.thisWeek || 0} cette semaine`}
          icon={Activity}
          color="blue"
          loading={loading}
        />
        <AIKPICard
          title="Entreprises IA"
          value={overview?.summary?.companiesWithAI || 0}
          subtitle={`sur ${overview?.summary?.totalProCompanies || 0} PRO`}
          icon={Building2}
          color="indigo"
          loading={loading}
        />
        <AIKPICard
          title="Taux d'adoption"
          value={`${overview?.summary?.aiAdoptionRate || 0}%`}
          subtitle="des comptes PRO"
          icon={TrendingUp}
          color="green"
          loading={loading}
        />
        <AIKPICard
          title="Coût mensuel"
          value={formatCurrency(overview?.costs?.estimatedMonthlyCost || 0)}
          subtitle="estimé"
          icon={DollarSign}
          color="amber"
          loading={loading}
        />
        <AIKPICard
          title="Coût/Analyse"
          value={formatCurrency(overview?.costs?.avgCostPerAnalysis || 0)}
          subtitle="moyenne"
          icon={BarChart3}
          color="red"
          loading={loading}
        />
      </div>

      {/* Top Companies & Analysis Types */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Companies */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-purple-500" />
              Top 5 - Utilisation IA
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="space-y-3">
                {[1, 2, 3, 4, 5].map(i => <Skeleton key={i} className="h-12 w-full" />)}
              </div>
            ) : overview?.topCompanies?.length > 0 ? (
              <div className="space-y-3">
                {overview.topCompanies.map((company, idx) => (
                  <div 
                    key={company.companyId}
                    className="flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors cursor-pointer"
                    onClick={() => setDetailsDialog({ open: true, companyId: company.companyId })}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-white ${
                        idx === 0 ? 'bg-amber-500' :
                        idx === 1 ? 'bg-slate-400' :
                        idx === 2 ? 'bg-amber-700' :
                        'bg-slate-300'
                      }`}>
                        {idx + 1}
                      </div>
                      <div>
                        <p className="font-medium text-slate-900">{company.companyName}</p>
                        <Badge variant="secondary" className="text-xs">{company.plan?.toUpperCase()}</Badge>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="font-bold text-purple-600">{company.analysisCount}</span>
                      <ChevronRight className="w-4 h-4 text-slate-400" />
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-slate-500">
                <Brain className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                <p>Aucune analyse enregistrée</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Analysis Types */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-blue-500" />
              Répartition par type
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="space-y-3">
                {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-8 w-full" />)}
              </div>
            ) : overview?.byType && Object.keys(overview.byType).length > 0 ? (
              <div className="space-y-4">
                {Object.entries(overview.byType).map(([type, count]) => {
                  const total = overview.summary?.totalAnalyses || 1;
                  const percentage = Math.round((count / total) * 100);
                  return (
                    <div key={type} className="space-y-2">
                      <div className="flex items-center justify-between">
                        <AnalysisTypeBadge type={type} />
                        <span className="text-sm font-medium">{count} ({percentage}%)</span>
                      </div>
                      <Progress value={percentage} className="h-2" />
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-8 text-slate-500">
                <BarChart3 className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                <p>Aucune donnée disponible</p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Monthly Trend */}
      {overview?.monthlyTrend && overview.monthlyTrend.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <LineChart className="w-5 h-5 text-indigo-500" />
              Tendance mensuelle (6 mois)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-end gap-4 h-40">
              {overview.monthlyTrend.map((item, idx) => {
                const maxCount = Math.max(...overview.monthlyTrend.map(m => m.count)) || 1;
                const height = (item.count / maxCount) * 100;
                return (
                  <div key={idx} className="flex-1 flex flex-col items-center">
                    <span className="text-sm font-bold text-slate-700 mb-1">{item.count}</span>
                    <div 
                      className="w-full bg-gradient-to-t from-purple-600 to-purple-400 rounded-t transition-all duration-500"
                      style={{ height: `${Math.max(height, 5)}%` }}
                    />
                    <span className="text-xs text-slate-500 mt-2">{item.month.slice(5)}</span>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Companies List */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="text-lg">Gestion IA par entreprise</CardTitle>
            <CardDescription>Activez ou désactivez l&apos;IA pour chaque entreprise PRO</CardDescription>
          </div>
          <Button 
            variant="outline" 
            className="text-red-600 border-red-200 hover:bg-red-50"
            onClick={() => setGlobalToggleDialog(true)}
          >
            <Power className="w-4 h-4 mr-2" />
            Action globale
          </Button>
        </CardHeader>
        <CardContent>
          {/* Filters */}
          <div className="flex flex-col md:flex-row gap-4 mb-6">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Rechercher une entreprise..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
              />
            </div>
            <Select value={aiFilter} onValueChange={setAiFilter}>
              <SelectTrigger className="w-[180px]">
                <Brain className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Statut IA" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous</SelectItem>
                <SelectItem value="enabled">IA activée</SelectItem>
                <SelectItem value="disabled">IA désactivée</SelectItem>
              </SelectContent>
            </Select>
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="w-[180px]">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Trier par" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="analysisCount">Total analyses</SelectItem>
                <SelectItem value="analysesThisMonth">Ce mois</SelectItem>
                <SelectItem value="companyName">Nom</SelectItem>
                <SelectItem value="lastAnalysisAt">Dernière analyse</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={fetchData} disabled={loading}>
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Actualiser
            </Button>
          </div>

          {/* Companies Table */}
          {loading ? (
            <div className="space-y-4">
              {[1, 2, 3, 4, 5].map(i => <Skeleton key={i} className="h-20 w-full" />)}
            </div>
          ) : sortedCompanies.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <Building2 className="w-12 h-12 mx-auto mb-4 text-slate-300" />
              <p>Aucune entreprise trouvée</p>
            </div>
          ) : (
            <div className="space-y-3">
              {sortedCompanies.map(company => (
                <div 
                  key={company.companyId}
                  className="flex items-center justify-between p-4 bg-slate-50 rounded-xl hover:bg-slate-100 transition-colors"
                >
                  <div className="flex items-center gap-4">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                      company.aiEnabled 
                        ? 'bg-gradient-to-br from-purple-500 to-purple-600' 
                        : 'bg-slate-300'
                    }`}>
                      <Brain className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-slate-900">{company.companyName}</h3>
                        <Badge className={company.aiEnabled ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-200 text-slate-700'}>
                          {company.aiEnabled ? 'IA Active' : 'IA Inactive'}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-4 mt-1 text-sm text-slate-500">
                        <span className="flex items-center gap-1">
                          <Brain className="w-3 h-3" />
                          {company.totalAnalyses} analyses
                        </span>
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {company.analysesThisMonth} ce mois
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {formatRelativeDate(company.lastAnalysisAt)}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <p className="text-sm font-medium text-slate-900">{formatCurrency(company.estimatedCostUSD)}</p>
                      <p className="text-xs text-slate-500">coût estimé</p>
                    </div>
                    
                    <Switch
                      checked={company.aiEnabled}
                      onCheckedChange={() => setToggleDialog({ open: true, company })}
                    />
                    
                    <Button 
                      variant="ghost" 
                      size="icon"
                      onClick={() => setDetailsDialog({ open: true, companyId: company.companyId })}
                    >
                      <Eye className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Dialogs */}
      <ToggleAIDialog
        isOpen={toggleDialog.open}
        onClose={() => setToggleDialog({ open: false, company: null })}
        company={toggleDialog.company}
        onSuccess={fetchData}
      />

      <CompanyAIDetailsDialog
        isOpen={detailsDialog.open}
        onClose={() => setDetailsDialog({ open: false, companyId: null })}
        companyId={detailsDialog.companyId}
      />

      <GlobalAIToggleDialog
        isOpen={globalToggleDialog}
        onClose={() => setGlobalToggleDialog(false)}
        currentlyEnabled={overview?.summary?.companiesWithAI > 0}
        onSuccess={fetchData}
      />
    </div>
  );
}
